export interface PlaceHit {
  lat: number;
  lng: number;
  label: string;
  detail: string;
}

export interface PlaceBias {
  lat: number;
  lng: number;
}

/** About 50 km around the car. A second, unbounded search runs only if this box is empty. */
const BIAS_DEG = 0.45;

/**
 * One Nominatim search. The public server forbids a lookup on every keystroke,
 * so this runs when the driver asks, not as they type.
 */
export function nominatimSearchUrl(query: string, bias?: PlaceBias): string {
  const url = new URL('https://nominatim.openstreetmap.org/search');
  url.searchParams.set('q', query);
  url.searchParams.set('format', 'jsonv2');
  url.searchParams.set('limit', '5');
  url.searchParams.set('addressdetails', '1');
  url.searchParams.set('dedupe', '1');
  if (bias && Number.isFinite(bias.lat) && Number.isFinite(bias.lng)) {
    const edge = (n: number) => n.toFixed(4);
    url.searchParams.set(
      'viewbox',
      `${edge(bias.lng - BIAS_DEG)},${edge(bias.lat + BIAS_DEG)},${edge(bias.lng + BIAS_DEG)},${edge(bias.lat - BIAS_DEG)}`,
    );
    // Soft preference still ranks a famous distant name above the shop down
    // the road. Lock the first pass to the box around the car.
    url.searchParams.set('bounded', '1');
  }
  return url.toString();
}

export function parseNominatimResults(data: unknown): PlaceHit[] {
  if (!Array.isArray(data)) return [];
  const hits: PlaceHit[] = [];
  for (const row of data) {
    if (typeof row !== 'object' || row === null) continue;
    const rec = row as Record<string, unknown>;
    const lat = typeof rec.lat === 'string' ? parseFloat(rec.lat) : Number.NaN;
    const lng = typeof rec.lon === 'string' ? parseFloat(rec.lon) : Number.NaN;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    const name = typeof rec.name === 'string' ? rec.name.trim() : '';
    const display = typeof rec.display_name === 'string' ? rec.display_name : '';
    const parts = display.split(',').map(part => part.trim()).filter(Boolean);
    const label = name || parts[0] || 'Place';
    const rest = parts[0]?.toLowerCase() === label.toLowerCase() ? parts.slice(1) : parts;
    hits.push({ lat, lng, label, detail: rest.slice(0, 2).join(', ') });
    if (hits.length === 5) break;
  }
  return hits;
}
